// MACHINERY V2 (Stroje) — slice -> toast binding for the Machinery surfaces
// (M2.2, review finding F2).
//
// >>> NEW SHARED V2 PRIMITIVE (host half). M2.3-M2.8 MOUNT THIS. <<<
//
// `customerMachinery` already carries `success` / `error`; every thunk
// rejection lands in `error` and handlers dispatch setSuccess/setError. This
// component is the v2 surface's RENDERER for that state — the job the live
// grid does at components/Machinery.tsx:1258-1268 and which no v2 page did.
//
// Mount it ONCE per v2 page that mutates machinery data. It covers, with no
// per-call-site work: duplicate failure, restore success/failure, decommission
// and group-assignment outcomes, and any other rejected thunk.
//
// WHY THE ERROR NORMALISER IS RESTATED HERE rather than importing
// `NotificationMessage` from shared/models/models.ts: that module's first two
// lines are `import { AlertColor, AlertPropsColorOverrides } from
// "@mui/material"`. They are type-only and TypeScript elides them, so the
// runtime bundle would be unaffected — but the v2 rule is about the IMPORT
// GRAPH, and a later edit turning one of those into a value import would drag
// MUI into v2 silently. The branches below mirror ApiError.create for the
// shapes a machinery thunk can actually reject with (Axios error, plain
// string, `{message}` body); anything else falls back to a translated default
// instead of ApiError's hard-coded English one.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React, { useCallback } from "react";

import { useAppDispatch, useAppSelector } from "../../../../shared/hooks/sharedHooks";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { setError, setSuccess } from "../../customerMachinerySlice";
import ToastV2 from "./ToastV2";

/** Best-effort human message from whatever a rejected thunk stored. */
export const toastErrorMessage = (error: any, fallback: string): string => {
  if (!error) return fallback;
  if (typeof error === "string") return error;
  const data = error?.response?.data;
  if (typeof data === "string" && data) return data;
  if (data?.message) return data.message;
  if (error?.message) return error.message;
  return fallback;
};

const MachineryToastHost: React.FC = () => {
  const dispatch = useAppDispatch();
  const { translate } = useTranslation("myMachinery");
  const success = useAppSelector((s) => s.customerMachinery.success);
  const error = useAppSelector((s) => s.customerMachinery.error);

  const closeSuccess = useCallback(() => dispatch(setSuccess(undefined)), [dispatch]);
  const closeError = useCallback(() => dispatch(setError(undefined)), [dispatch]);

  return (
    <>
      <ToastV2
        open={!!error}
        variant="error"
        message={toastErrorMessage(error, translate("myMachinery:v2.fleet.genericError"))}
        onClose={closeError}
      />
      {/* Errors take the top slot; a success opened at the same time stacks
          below it instead of painting over it. */}
      <ToastV2
        open={!!success}
        variant="success"
        message={success || ""}
        onClose={closeSuccess}
        stackIndex={error ? 1 : 0}
      />
    </>
  );
};

export default MachineryToastHost;
